import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';

import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';

import { Drawer } from 'ui-components/components/drawer/drawer';
import { DrawerHeader } from 'ui-components/components/drawer/drawer-header';
import { FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import AdvancedShippingNoticeContextProvider, { useAdvancedShippingNoticeContext } from './advanced-shipping-notice-context';
import styles from './advanced-shipping-notice-drawer.module.less';
import { messages } from './messages';
import { AsnStatus, SubmittedAsnStatuses } from '../../constants';
import { useAsnGridContext } from '../advanced-shipping-notice-grid/context';
import { AsnStatusTag } from '../asn-status-tag';
import { AlmostDoneCard } from './steps/almost-done/almost-done-card';
import { SubmitAsnBanner } from './steps/components/submit-asn-banner';
import { SETUP_CONFIGURATION_STEP, SELECT_PRODUCTS_STEP, DEFINE_SHIPMENTS_STEP } from './steps/constants';
import { DefineShipmentsCard } from './steps/define-shipments/define-shipments-card';
import { SelectProductsCard } from './steps/select-products/select-products-card';
import { SetupConfigurationCard } from './steps/setup-configurations/setup-configurations-card';
import { useSellerFormattedDateTime } from '../../../../../hooks/use-datetime-formats';
import { WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';

const ASNMetadata = () => {
  const { isDraft, initialAsn, savedPayloads, currentAsnStatus } = useAdvancedShippingNoticeContext();
  const sellerFormattedCreateDate = useSellerFormattedDateTime(initialAsn.createdAt);

  return (
    <>
      <div className={styles['metadata-row']}>
        {sellerFormattedCreateDate ? (
          <Text formattedMessageValues={{ date: sellerFormattedCreateDate }} textType={TextType.Paragraph} value={messages.createdAt} />
        ) : null}
        {savedPayloads[SETUP_CONFIGURATION_STEP]?.purchaseOrderNumber ? (
          <Text
            formattedMessageValues={{
              orderNumber: savedPayloads[SETUP_CONFIGURATION_STEP].purchaseOrderNumber,
            }}
            textType={TextType.Paragraph}
            value={messages.purchaseOrderNumber}
          />
        ) : null}
        {initialAsn.partnerAsnId ? (
          <Text
            formattedMessageValues={{
              receivingId: initialAsn.partnerAsnId,
            }}
            textType={TextType.Paragraph}
            value={messages.receivingId}
          />
        ) : null}
        <AsnStatusTag status={currentAsnStatus} />
      </div>
      {isDraft ? <Text textType={TextType.Paragraph} value={messages.createAdvancedShippingNoticeSubtitle} /> : null}
    </>
  );
};

const getHeaderTextDescriptor = (status) => {
  if (status === AsnStatus.Draft) {
    return messages.createAdvancedShippingNoticeTitle;
  }
  if (SubmittedAsnStatuses[status]) {
    return messages.submittedAdvancedShippingNoticeTitle;
  }
  if (status === AsnStatus.Cancelled) {
    return messages.cancelledAdvancedShippingNoticeTitle;
  }
  if (status === AsnStatus.Completed) {
    return messages.completedAdvancedShippingNoticeTitle;
  }
};
const AsnDrawerHeader = () => {
  const { currentAsnStatus } = useAdvancedShippingNoticeContext();
  const drawerHeaderTextDescriptor = getHeaderTextDescriptor(currentAsnStatus);
  return (
    <DrawerHeader
      closeButtonAriaLabel={messages.closeDrawerButtonAriaLabel}
      title={<Text textType={TextType.Paragraph} value={drawerHeaderTextDescriptor} />}
    />
  );
};

const getBodyTitleDescriptor = (status) => {
  if (status === AsnStatus.Draft) {
    return messages.createAdvancedShippingNoticeTitle;
  }
  if (SubmittedAsnStatuses[status]) {
    return messages.receivedAdvancedShippingNoticeSubtitle;
  }
  if (status === AsnStatus.Cancelled) {
    return messages.cancelledAdvancedShippingNoticeSubtitle;
  }
  if (status === AsnStatus.Completed) {
    return messages.completedAdvancedShippingNoticeSubtitle;
  }
};
const DrawerBodyTitleText = () => {
  const { currentAsnStatus } = useAdvancedShippingNoticeContext();
  const drawerBodyTitleText = getBodyTitleDescriptor(currentAsnStatus);
  return (
    <FlexColumn className={styles['title-row']}>
      <Text
        className={styles['drawer-body-title']}
        formattedMessageValues={{
          icon: <FontAwesomeIcon className={styles['asn-received-icon']} icon={faCheckCircle} />,
        }}
        textType={TextType.H3}
        value={drawerBodyTitleText}
      />
      <ASNMetadata />
    </FlexColumn>
  );
};

export const AdvancedShippingNoticeDrawer = ({ closeDrawer, initialAsn, trackAsnWorkflow }) => {
  const selectProductsRef = useRef(null);
  const defineShipmentsRef = useRef(null);
  const { loadAsnGridData } = useAsnGridContext();

  const [nextStepId, setNextStepId] = useState(null);
  const [shouldScrollToTop, setShouldScrollToTop] = useState(false);

  useEffect(() => {
    if (!nextStepId) return;

    const scrollOptions = { behavior: 'smooth', block: 'start' };

    switch (nextStepId) {
      case SELECT_PRODUCTS_STEP:
        selectProductsRef.current?.scrollIntoView?.(scrollOptions);
        break;
      case DEFINE_SHIPMENTS_STEP:
        defineShipmentsRef.current?.scrollIntoView?.(scrollOptions);
        break;
      default:
        break;
    }

    // Reset nextStepId after scrolling
    setNextStepId(null);
  }, [nextStepId]);

  useLayoutEffect(() => {
    if (shouldScrollToTop) {
      const drawerBackground = document.querySelector(`.${styles['drawer-background']}`);
      if (drawerBackground) {
        drawerBackground.scrollTop = 0;
      }
      setShouldScrollToTop(false);
    }
  }, [shouldScrollToTop]);

  const handleScrollToNextSection = (stepId) => {
    if (stepId === null) {
      setShouldScrollToTop(true);
      return;
    }
    setNextStepId(stepId);
  };

  const onClose = useCallback(() => {
    loadAsnGridData();
    trackAsnWorkflow(WorkflowSteps.CLOSE);
    closeDrawer();
  }, [closeDrawer, loadAsnGridData, trackAsnWorkflow]);

  return (
    <AdvancedShippingNoticeContextProvider
      initialAsn={initialAsn}
      scrollToNextSection={handleScrollToNextSection}
      trackAsnWorkflow={trackAsnWorkflow}
    >
      <Drawer onCancel={onClose} onClose={onClose}>
        <AsnDrawerHeader />
        <div className={styles['drawer-background']}>
          <FlexColumn className={styles['drawer-body']}>
            <DrawerBodyTitleText />
            <AlmostDoneCard />
            <SetupConfigurationCard />
            <div ref={selectProductsRef}>
              <SelectProductsCard />
            </div>
            <div ref={defineShipmentsRef}>
              <DefineShipmentsCard />
            </div>
          </FlexColumn>
        </div>
        <SubmitAsnBanner />
      </Drawer>
    </AdvancedShippingNoticeContextProvider>
  );
};
